import { TextArea, Text, Flex } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";

import { updateSkillRequiredInputs } from "../../../services/skill-documents";

const DEBOUNCE_MS = 500;

export interface RequiredInputsEditorProps {
  skillId: string;
  skillType: "builtin" | "custom";
  description: string;
  onDescriptionChange: (description: string) => void;
  disabled?: boolean;
}

export const RequiredInputsEditor = ({
  skillId,
  skillType,
  description,
  onDescriptionChange,
  disabled = false,
}: RequiredInputsEditorProps) => {
  const [localValue, setLocalValue] = useState(description);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalValue(description);
  }, [description]);

  // Cancel any pending save on unmount so a stale skillId/skillType from a
  // previous selection can't fire after the user has switched skills.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      onDescriptionChange(val);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          await updateSkillRequiredInputs({
            ...(skillType === "builtin"
              ? { skillId }
              : { workflowId: skillId }),
            requiredInputsDescription: val,
          });
        } catch {
          // fail silently; user can retry
        }
      }, DEBOUNCE_MS);
    },
    [skillId, skillType, onDescriptionChange],
  );

  return (
    <Flex direction="column" gap="2">
      <Text size="2" color="gray">
        Describe what information the agent should collect from the user before
        starting this skill. The agent will ask for these inputs
        conversationally at the start of each session.
      </Text>
      <TextArea
        placeholder="e.g. Ask for the product name, target market, and campaign objective before starting the review."
        value={localValue}
        onChange={handleChange}
        rows={3}
        disabled={disabled}
        data-testid={`required-inputs-editor-${skillId}`}
      />
    </Flex>
  );
};
